import isNil from 'lodash/isNil';
import includes from 'lodash/includes';
import { Observable, Subject, Subscription } from 'rxjs';
import _Pusher from 'pusher-js';
import { NgZone } from '@angular/core';
import { ServerApi } from '@services/server-api';
import { inDemoMode, inReadOnlyMode, inSupportMode } from '@lib/remote-login';

const CLIENT_ONLY_CHANNELS = ['hijack', 'realtime-aux-data'];
const PUSHER_KEEP_ALIVE_PING_INTERVAL: number = 5 * 60 * 1000; // 5 mins

export class BulkPusherChannelWrapper<TId, TJoinData, TJoinResponse, TEventData> {
  private channelNameSuffix: string;
  private pusher;
  private api: ServerApi;
  private zone: NgZone;
  private channels = new Map<string, any>();
  private keepAliveTimeout;
  private joinEndPointPath = 'callbacks/pusher/join';
  private joinData: TJoinData;
  private joinApiResultStream = new Subject<TJoinResponse>();
  private joinChannelSubscription: Subscription;
  private channelNamePrefix: string;
  private pusherKeepAlivePingInterval: number;

  constructor(config: {
    pusher
    api: ServerApi;
    zone: NgZone;
    channelNamePrefix: string;
    channelNameSuffix: string;
    ids: TId[];
    joinData: TJoinData;
    endPointPathPostfix?: string;
    enablePusherInHijack: boolean;
    pusherKeepAliveInterval?: number;
  }) {
    this.channelNameSuffix = config.channelNameSuffix;
    this.pusher = config.pusher;
    this.api = config.api;
    this.zone = config.zone;
    config.endPointPathPostfix = isNil(config.endPointPathPostfix) ? '' : config.endPointPathPostfix;
    this.joinEndPointPath += config.endPointPathPostfix;
    this.joinData = config.joinData;
    this.channelNamePrefix = config.channelNamePrefix;
    this.subscribeToChannels(config.ids);
    this.joinChannel(config.enablePusherInHijack);
    this.pusherKeepAlivePingInterval = config.pusherKeepAliveInterval || PUSHER_KEEP_ALIVE_PING_INTERVAL;
  }


  public getEvent(id: TId, eventName: string): Observable<TEventData>  {
    const channelName = this.getPusherChannelName(id);
    return this.getChannelEventStream(channelName, eventName);
  }

  public updateJoinData(newData: TJoinData) {
    this.joinData = newData;
  }

  public get joinApiResultStream$(): Observable<TJoinResponse> {
    return this.joinApiResultStream.asObservable();
  }

  public joinPusherChannels(ids: TId[]): void {
    if (isNil(ids) || ids.length === 0) return;
    this.subscribeToChannels(ids);
  }

  public retriggerJoinChannel(enablePusherInHijack: boolean): void {
    clearTimeout(this.keepAliveTimeout);
    this.joinChannelSubscription.unsubscribe();
    this.joinChannel(enablePusherInHijack);
  }

  public unsubscribe() {
    if (this.joinChannelSubscription)  this.joinChannelSubscription.unsubscribe();
    if (this.keepAliveTimeout) clearTimeout(this.keepAliveTimeout);
    this.channels.forEach((channel, channelName) => {
      this.pusher.unsubscribe(channelName);
    });
    this.channels.clear();
  }

  private joinChannel(enablePusherInHijack: boolean, retryAttempt = 1) {
    if (
      ((inSupportMode() || inDemoMode() || inReadOnlyMode()) && !enablePusherInHijack) ||
      includes(CLIENT_ONLY_CHANNELS, this.channelNameSuffix)
    ) return;
    this.joinChannelSubscription = this.api.post(this.joinEndPointPath, this.joinData).subscribe((response) => {
      if (this.joinApiResultStream) {
        this.joinApiResultStream.next(response);
      }
      this.keepAliveTimeout = setTimeout(() => this.joinChannel(enablePusherInHijack), this.pusherKeepAlivePingInterval);
    }, () => {
      this.keepAliveTimeout = setTimeout(() => this.joinChannel(enablePusherInHijack, retryAttempt + 1), retryAttempt * 2000);
    });
  }

  private getChannelEventStream(channelName: string, eventName: string): Observable<TEventData> {
    return new Observable<TEventData>(observer => {
      const channel = this.channels.get(channelName);
      if (!channel) {
        throw new Error(`Channel ${channelName} is not subscribed.`);
      }
      const callback = (data: TEventData) => this.zone.run(() => observer.next(data));
      channel.bind(eventName, callback);
      return () => {
        channel.unbind(eventName, callback);
        this.pusher.unsubscribe(channelName);
        this.channels.delete(channelName);
      };
    });
  }

  private getPusherChannelName(id: TId): string {
    return `${this.channelNamePrefix}-${id}-${this.channelNameSuffix}`;
  }

  private subscribeToChannels(ids: TId[]): void {
    ids.forEach(id => {
      const channelName = this.getPusherChannelName(id);
      this.channels.set(channelName, this.pusher.subscribe(channelName));
    });
  }
}
